import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "./client";
import type { CatalogCard, CollectionEntry } from "./client";

export interface CatalogState {
  catalog: CatalogCard[];
  catalogById: Map<string, CatalogCard>;
  collection: CollectionEntry[];
  owned: Record<string, number>;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

// ---- Module-level cache (mirrors the /api/me cache in client.ts) ----
//
// One copy of the owned catalog + collection shared by every mount of
// `useCatalogAndCollection`. The first mount fetches; later mounts
// (lobby → deck builder → battle) render from cache with no network
// call. `reload()` forces a fresh fetch and notifies every mounted
// subscriber. Ownership-changing mutations (pack opens, shop card
// buys, admin grants) refresh the cache through the invalidator
// registered with `client.ts` below — registration rather than a
// direct import avoids a client ↔ useCatalog module cycle.

interface CatalogCacheData {
  catalog: CatalogCard[];
  collection: CollectionEntry[];
}

let cache: CatalogCacheData | null = null;
let inflight: Promise<CatalogCacheData> | null = null;
const listeners = new Set<(data: CatalogCacheData) => void>();

/** Subscribe to cache updates. Returns an unsubscribe function. */
export function subscribeCatalogCache(
  listener: (data: CatalogCacheData) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

async function fetchPair(): Promise<CatalogCacheData> {
  const [c, col] = await Promise.all([
    api.getOwnedCatalog(),
    api.getCollection(),
  ]);
  const data = { catalog: c.cards, collection: col.entries };
  cache = data;
  for (const l of listeners) l(data);
  return data;
}

function startFetch(): Promise<CatalogCacheData> {
  const p: Promise<CatalogCacheData> = fetchPair().finally(() => {
    // A newer fetch may have replaced us (forced refetch); only
    // clear the slot if we still own it.
    if (inflight === p) inflight = null;
  });
  inflight = p;
  return p;
}

/** Cached read: returns the cache when warm, otherwise shares the
 *  in-flight request (or starts one). */
export function loadCatalogAndCollection(): Promise<CatalogCacheData> {
  if (cache) return Promise.resolve(cache);
  return inflight ?? startFetch();
}

/** Force a fresh fetch. Stale data stays visible until it lands. */
export function refetchCatalogAndCollection(): Promise<CatalogCacheData> {
  return startFetch();
}

/** Background refresh after a mutation that granted cards. No-op if
 *  nothing has ever loaded the catalog (nothing to go stale). Errors
 *  are swallowed — the old cache simply remains until the next read. */
export function invalidateCatalogCache(): void {
  if (cache == null && inflight == null) return;
  refetchCatalogAndCollection().catch(() => {});
}

api.setCatalogInvalidator(invalidateCatalogCache);

/** Test-only: drop all module state between specs. */
export function __resetCatalogCacheForTests(): void {
  cache = null;
  inflight = null;
  listeners.clear();
}

const EMPTY_CATALOG: CatalogCard[] = [];
const EMPTY_COLLECTION: CollectionEntry[] = [];

// Owned-only catalog + collection counts, with an O(1) id → card map.
// `/api/catalog` is admin-only — exposing unowned cards leaks pool info.
export function useCatalogAndCollection(): CatalogState {
  const [data, setData] = useState<CatalogCacheData | null>(cache);
  const [loading, setLoading] = useState(cache == null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeCatalogCache((d) => {
      setData(d);
      setLoading(false);
      setError(null);
    });
    if (cache) {
      // The cache may have refreshed between render and this effect.
      setData(cache);
      setLoading(false);
    } else {
      loadCatalogAndCollection().catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    }
    return unsubscribe;
  }, []);

  const reload = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      await refetchCatalogAndCollection();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const catalog = data?.catalog ?? EMPTY_CATALOG;
  const collection = data?.collection ?? EMPTY_COLLECTION;

  const catalogById = useMemo(() => {
    const m = new Map<string, CatalogCard>();
    for (const c of catalog) m.set(c.id, c);
    return m;
  }, [catalog]);

  const owned = useMemo(() => {
    const m: Record<string, number> = {};
    for (const e of collection) m[e.card_id] = e.count;
    return m;
  }, [collection]);

  return { catalog, catalogById, collection, owned, loading, error, reload };
}
